import { Accommodation } from '../../types/accommodation'

export type SiteKeyAccommodationOption = { value: string; label: string }

/**
 * Accommodation choices for a site key's booking redirects and associated gateways.
 *
 * The value is the raw property code (`displayPropertyCode`), never `propertyCode`: the BFF
 * encrypts `propertyCode` into an `acc_…` token on the way out, but never decrypts a field named
 * `Code` on the way in, so the token would be stored as-is. The asset service then compares it with
 * the booking's property code, finds nothing, and the booking page reports "no gateways found".
 * An accommodation without a raw code is left out rather than offered with a token.
 */
export const siteKeyAccommodationOptions = (
  accommodations: Accommodation[] | undefined,
  defaultLabel: string,
  lang: string
): SiteKeyAccommodationOption[] => [
  { value: 'Default', label: defaultLabel },
  ...(accommodations ?? [])
    .filter((acc) => !!acc.basicDetails.displayPropertyCode)
    .map((acc) => ({
      value: acc.basicDetails.displayPropertyCode as string,
      label:
        lang === 'fa'
          ? acc.basicDetails.nameTranslatedList[0].value
          : acc.basicDetails.name
    }))
]

/**
 * The raw property code for a stored site-key `Code`. Site keys saved before the fix above hold
 * the accommodation's `acc_…` token; map it back so the row shows its accommodation again and the
 * next save stores the raw code. Any other code is returned unchanged.
 */
export const rawSiteKeyCode = (
  code: string,
  accommodations: Accommodation[] | undefined
): string => {
  const legacy = (accommodations ?? []).find(
    (acc) => code !== '' && acc.basicDetails.propertyCode === code
  )

  return legacy?.basicDetails.displayPropertyCode || code
}
